import React, { useState, useEffect, useRef } from 'react';
import { evenementsAPI } from '../services/api';
import {
    CATEGORIES_AMORCAGE, CODE_FILET, iconeEvenement, couleurEvenement
} from '../services/evenementsCategories';
import PanneauFlottant from './PanneauFlottant';

/** Échappement pour les fragments HTML construits à la main (fantôme de glisser). */
const echapperHtml = (s) => String(s || '').replace(/[&<>"]/g,
    c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

/**
 * Contenu du fantôme de glisser-déposer : l'image de l'événement s'il en a une,
 * sinon son icône FontAwesome. Le fantôme est un vrai nœud du document, donc la
 * police y est chargée — contrairement à la fenêtre d'impression de l'export.
 */
function fantomeContenu(evt, avecLibelle, categories) {
    const visuel = evt?.logo_url
        ? '<img src="' + echapperHtml(evt.logo_url) + '" alt="" style="width:14px;height:14px;object-fit:contain;vertical-align:middle;">'
        : '<i class="fa-solid ' + echapperHtml(iconeEvenement(evt, categories)) + '"></i>';
    return avecLibelle ? visuel + ' ' + echapperHtml(evt?.libelle || '') : visuel;
}

function EventManager({ config, isOpen, onClose, onEventsChange, highlightEventId, anneeId, annees, onDemanderConfirmation, categories }) {
    // Lot 5.4 ter : les catégories sont le RÉFÉRENTIEL du département
    // (`evenements_categories`, v36), plus une constante figée dans le code. Elles
    // portent le module lié, le symbole et la couleur — ce qui obligeait auparavant
    // à classer 33 événements par an au lieu de 10 catégories une fois.
    // Repli sur la liste d'amorçage tant que le chargement n'a pas répondu : le
    // panneau doit rester utilisable, pas afficher une liste vide.
    const CATEGORIES = categories?.length ? categories : CATEGORIES_AMORCAGE;
    const [evenements, setEvenements] = useState([]);
    const [newLibelle, setNewLibelle] = useState('');
    const [newCategorie, setNewCategorie] = useState(CODE_FILET);
    const [erreurCreation, setErreurCreation] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editingLibelle, setEditingLibelle] = useState('');
    const [loading, setLoading] = useState(false);
    const [draggedEvent, setDraggedEvent] = useState(null);
    const [hoveredPlacedId, setHoveredPlacedId] = useState(null);
    const [highlightId, setHighlightId] = useState(null);
    const dragGhostRef = useRef(null);
    const highlightRef = useRef(null);
    const createInputRef = useRef(null);

    // Récup N-1
    const [showRecup, setShowRecup] = useState(false);
    const [prevEvents, setPrevEvents] = useState([]);
    const [selectedRecupIds, setSelectedRecupIds] = useState([]);
    const [loadingRecup, setLoadingRecup] = useState(false);
    const [importingRecup, setImportingRecup] = useState(false);
    const [erreurRecup, setErreurRecup] = useState('');

    const currentAnneeId = anneeId || config?.anneeScolaireId;
    let prevAnneeId = null;
    if (annees && currentAnneeId) {
        const cur = annees.find(a => a.id === currentAnneeId);
        if (cur?.libelle) {
            const startYear = parseInt(cur.libelle.split('-')[0]);
            const prevLibelle = `${startYear - 1}-${startYear}`;
            const prev = annees.find(a => a.libelle === prevLibelle);
            prevAnneeId = prev?.id || null;
        }
    }

    const loadPrevEvents = async () => {
        if (!prevAnneeId) return;
        setLoadingRecup(true);
        try {
            const res = await evenementsAPI.getConfig(config.departement.id, { anneeScolaireId: prevAnneeId });
            const evts = res.data.evenements || [];
            setPrevEvents(evts);
            setSelectedRecupIds(evts.map(e => e.id));
        } catch (err) {
            console.error('Erreur chargement N-1:', err);
        } finally {
            setLoadingRecup(false);
        }
    };

    const handleOpenRecup = () => {
        setShowRecup(true);
        loadPrevEvents();
    };

    const toggleRecupId = (id) => {
        setSelectedRecupIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const handleImportRecup = async () => {
        const toImport = prevEvents.filter(e =>
            selectedRecupIds.includes(e.id) && !evenements.some(ex => ex.libelle === e.libelle)
        );
        setImportingRecup(true);
        setErreurRecup('');
        try {
            for (const evt of toImport) {
                // ⚠️ IL Y AVAIT DEUX PORTES « RÉCUPÉRER N-1 », et le classement d'une
                // année survivait SELON LE BOUTON SUR LEQUEL ON AVAIT CLIQUÉ : celle
                // du Paramétrage recopiait tout, celle-ci perdait le rattachement en
                // silence. Réparé au lot 5.4 bis en recopiant six champs des deux
                // côtés — et rendu IMPOSSIBLE au lot 5.4 ter, où il n'y a plus qu'une
                // porte (celle-ci) et où le module, le symbole et la couleur vivent
                // sur la CATÉGORIE, qui ne dépend pas de l'année. Il ne reste donc
                // qu'un code de catégorie à transmettre, plus une liste de champs
                // qu'on peut oublier d'allonger.
                //
                // `couleur`, `icone` et `logoUrl` ne sont transmis que s'ils sont
                // renseignés : ce sont des DÉROGATIONS. Les recopier vides
                // redonnerait à chaque événement une apparence propre, c'est-à-dire
                // exactement ce que la v36 a défait.
                await evenementsAPI.createConfig({
                    departementId: config.departement.id,
                    libelle: evt.libelle,
                    categorie: evt.categorie,
                    couleur: evt.couleur || '',
                    icone: evt.icone || '',
                    logoUrl: evt.logo_url || '',
                    anneeScolaireId: currentAnneeId
                });
            }
            setShowRecup(false);
            await loadEvenements();
        } catch (err) {
            // Un échec de récupération doit se voir. Il partait dans la console :
            // le panneau se refermait comme si tout s'était bien passé (leçon du
            // lot 5.3 ter — un échec muet est pire qu'une erreur affichée).
            setErreurRecup(
                err?.response?.data?.error
                || "La récupération a échoué. Rien n'a peut-être été importé — vérifiez la liste."
            );
        } finally {
            setImportingRecup(false);
        }
    };

    // La coquille du panneau — déplacement, redimensionnement, position d'ouverture
    // — a été extraite dans `PanneauFlottant` au lot 5.6, qui en compte trois de
    // plus sur l'écran d'export. Ce fichier ne garde que la référence, dont il a
    // besoin pour mesurer sa propre boîte pendant un glisser-déposer.
    const panelRef = useRef(null);

    useEffect(() => {
        if (isOpen && config?.departement?.id) {
            loadEvenements();
        }
    }, [isOpen, config?.departement?.id, anneeId]);

    // Gérer le highlight quand on clique sur une pastille
    useEffect(() => {
        if (highlightEventId) {
            setHighlightId(highlightEventId);
            // Scroll vers l'élément mis en surbrillance
            setTimeout(() => {
                if (highlightRef.current) {
                    highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 100);
            // Retirer la surbrillance après 3 secondes
            const timer = setTimeout(() => setHighlightId(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [highlightEventId]);

    // Exposer le reload
    useEffect(() => {
        window._reloadEventManager = loadEvenements;
        return () => { delete window._reloadEventManager; };
    }, [config]);

    const loadEvenements = async () => {
        try {
            setLoading(true);
            const response = await evenementsAPI.getConfig(config.departement.id, {
                anneeScolaireId: anneeId || config.anneeScolaireId
            });
            const evts = response.data.evenements || [];
            setEvenements(evts);
            if (onEventsChange) onEventsChange(evts);
        } catch (err) {
            console.error('Erreur chargement événements:', err);
        } finally {
            setLoading(false);
        }
    };

    // Lot 5.4 bis : l'apparence se lit sur L'ÉVÉNEMENT (icône et couleur choisies
    // dans le référentiel), avec repli sur le module lié puis sur la catégorie.
    // Auparavant elle était dérivée de la seule catégorie : un événement personnalisé
    // se serait affiché correctement partout sauf dans ce panneau.
    const getCatInfo = (evt) => ({
        icone: iconeEvenement(evt, CATEGORIES),
        couleur: couleurEvenement(evt, CATEGORIES),
        libelle: (CATEGORIES.find(c => c.code === (evt.categorie || CODE_FILET)) || {}).libelle || 'Autre'
    });

    const handleCreate = async () => {
        if (!newLibelle.trim()) {
            createInputRef.current?.focus();
            return;
        }
        setErreurCreation('');
        try {
            await evenementsAPI.createConfig({
                departementId: config.departement.id,
                libelle: newLibelle.trim(),
                categorie: newCategorie,
                anneeScolaireId: anneeId || config.anneeScolaireId
            });
            setNewLibelle('');
            setNewCategorie(CODE_FILET);
            await loadEvenements();
        } catch (err) {
            // Un échec de création doit se voir : il partait dans la console, et le
            // champ se vidait comme si l'événement avait été créé.
            setErreurCreation(err?.response?.data?.error || "L'événement n'a pas pu être créé.");
        }
    };

    const handleRename = async (id) => {
        if (!editingLibelle.trim()) return;
        try {
            await evenementsAPI.updateConfig(id, { libelle: editingLibelle.trim() });
            setEditingId(null);
            setEditingLibelle('');
            await loadEvenements();
        } catch (err) {
            console.error('Erreur renommage:', err);
        }
    };

    const handleDelete = (id) => {
        const evt = evenements.find(e => e.id === id);
        onDemanderConfirmation({
            titre: 'Supprimer un événement',
            message: <>Supprimer définitivement <strong>{evt?.libelle || 'cet événement'}</strong> ? Il disparaît de la liste des événements types du département, avec sa date si elle est posée.</>,
            libelleConfirmer: 'Supprimer',
            danger: true,
            action: async () => {
                try {
                    await evenementsAPI.deleteConfig(id);
                    await loadEvenements();
                } catch (err) {
                    console.error('Erreur suppression:', err);
                }
            }
        });
    };

    const handleDissocier = async (evt) => {
        try {
            await evenementsAPI.deleteDate(evt.date_id);
            await loadEvenements();
        } catch (err) {
            console.error('Erreur dissociation:', err);
        }
    };

    const startEdit = (evt) => {
        setEditingId(evt.id);
        setEditingLibelle(evt.libelle);
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditingLibelle('');
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    };

    const formatDateShort = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
    };

    // Drag depuis la popup
    const handleDragStart = (e, evt) => {
        setDraggedEvent(evt);
        const ghost = document.createElement('div');
        ghost.className = 'event-drag-ghost';
        const cat = getCatInfo(evt);
        ghost.style.cssText = 'position:fixed;pointer-events:none;z-index:10000;background:' + cat.couleur + ';color:white;padding:6px 12px;border-radius:20px;font-size:12px;font-weight:700;box-shadow:0 4px 12px rgba(0,0,0,0.3);white-space:nowrap;transition:all 0.3s ease;';
        // ⚠️ DÉFAUT CORRIGÉ AU LOT 5.4 bis. C'était `textContent = cat.icone + …`,
        // et depuis le lot 5.2 `cat.icone` vaut « fa-star » : le fantôme de
        // glisser-déposer affichait donc la CLASSE EN TOUTES LETTRES. Invisible à
        // l'auto-contrôle, visible dès qu'on glisse un événement sur le calendrier.
        ghost.innerHTML = fantomeContenu(evt, true, CATEGORIES);
        document.body.appendChild(ghost);
        dragGhostRef.current = ghost;

        const emptyImg = new Image();
        emptyImg.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs=';
        e.dataTransfer.setDragImage(emptyImg, 0, 0);
        e.dataTransfer.setData('application/json', JSON.stringify({ eventId: evt.id, type: 'new-event' }));
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDrag = (e) => {
        if (!dragGhostRef.current) return;
        if (e.clientX === 0 && e.clientY === 0) return;
        const ghost = dragGhostRef.current;
        ghost.style.left = (e.clientX + 12) + 'px';
        ghost.style.top = (e.clientY - 15) + 'px';

        const panel = panelRef.current;
        if (panel) {
            const rect = panel.getBoundingClientRect();
            const isOutside = e.clientX < rect.left || e.clientX > rect.right ||
                              e.clientY < rect.top || e.clientY > rect.bottom;
            if (isOutside) {
                ghost.style.padding = '3px';
                ghost.style.fontSize = '10px';
                ghost.style.borderRadius = '50%';
                ghost.style.width = '20px';
                ghost.style.height = '20px';
                ghost.style.overflow = 'hidden';
                ghost.style.display = 'flex';
                ghost.style.alignItems = 'center';
                ghost.style.justifyContent = 'center';
                ghost.innerHTML = fantomeContenu(draggedEvent, false, CATEGORIES);
            } else {
                ghost.style.padding = '6px 12px';
                ghost.style.fontSize = '12px';
                ghost.style.borderRadius = '20px';
                ghost.style.width = 'auto';
                ghost.style.height = 'auto';
                ghost.style.overflow = 'visible';
                ghost.style.display = 'block';
                ghost.innerHTML = fantomeContenu(draggedEvent, true, CATEGORIES);
            }
        }
    };

    const handleDragEnd = () => {
        setDraggedEvent(null);
        if (dragGhostRef.current) {
            dragGhostRef.current.remove();
            dragGhostRef.current = null;
        }
    };

    useEffect(() => {
        window.addEventListener('drag', handleDrag);
        window.addEventListener('dragend', handleDragEnd);
        return () => {
            window.removeEventListener('drag', handleDrag);
            window.removeEventListener('dragend', handleDragEnd);
        };
    }, [draggedEvent]);

    // `PanneauFlottant` ne monte rien quand il est fermé ; ce garde-fou reste ici
    // pour que les listes ci-dessous ne soient pas calculées pour rien.
    if (!isOpen) return null;

    const nonPlaces = evenements.filter(e => !e.date_debut);
    const places = evenements.filter(e => e.date_debut);

    const isHighlighted = (id) => highlightId === id;

    return (
        // Lot 5.5 bis : le panneau a pris la forme du `floatp` de la maquette —
        // en-tête dégradé qui sert de poignée, sous-barre de création, zone de
        // liste défilante — et est devenu REDIMENSIONNABLE par son coin bas-droit.
        // Lot 5.6 : cette coquille est partagée avec les trois panneaux de l'écran
        // d'export, d'où le composant plutôt que le balisage recopié.
        <PanneauFlottant
            ouvert={isOpen}
            titre="Événements"
            icone="fa-thumbtack"
            largeur={440}
            hauteur="74vh"
            ancre="droite"
            hautInitial={Math.max(20, Math.round((window.innerHeight - 500) / 2))}
            refExterne={panelRef}
            onFermer={onClose}
            entete={
                <span className="pl-floatp-compteurs">
                    {nonPlaces.length > 0 && <span className="pl-badge pl-badge-warn">{nonPlaces.length}</span>}
                    {places.length > 0 && <span className="pl-badge pl-badge-ok">{places.length}</span>}
                </span>
            }
            sousBarre={<>
                <div className="pl-evt-create">
                    {/* ── CETTE LISTE PROPOSE LES CATÉGORIES, ET C'EST VOULU ────────
                        Elle affichait « fa-thumbtack Autre » : le nom de la classe
                        FontAwesome écrit en toutes lettres, depuis le lot 5.2 qui a
                        remplacé les emoji dans la VALEUR sans toucher aux endroits qui
                        l'affichent. Corrigé ici — le libellé SEUL, parce qu'un
                        `<option>` HTML ne peut pas contenir d'icône. Ce n'est pas un
                        oubli, c'est une limite du navigateur.

                        ⚠️ J'avais remplacé cette liste par celle des MODULES, au motif
                        que créer depuis ce panneau produisait un événement non classé.
                        Bertrand a corrigé : c'est bien la CATÉGORIE qu'on choisit ici —
                        c'est elle qui portera le module, le symbole et la couleur
                        (lot 5.4 ter, table `evenements_categories`). Le raisonnement
                        était bon, il visait le mauvais niveau : mettre le module sur
                        chaque événement, c'est classer 33 objets par an au lieu de 10
                        une fois pour toutes. */}
                    <select
                        value={newCategorie}
                        onChange={(e) => setNewCategorie(e.target.value)}
                        className="pl-f pl-evt-cat"
                        title="Catégorie de l'événement — elle lui donnera son symbole et sa couleur"
                    >
                        {CATEGORIES.map(cat => (
                            <option key={cat.code} value={cat.code}>{cat.libelle}</option>
                        ))}
                    </select>
                    <input
                        ref={createInputRef}
                        type="text" value={newLibelle}
                        onChange={(e) => setNewLibelle(e.target.value)}
                        placeholder="Nom de l'événement..."
                        className="pl-f pl-evt-nom"
                        onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                    />
                    <button className="pl-btn pl-btn-primary pl-btn-sm" onClick={handleCreate} title="Ajouter l'événement"><i className="fa-solid fa-plus" /></button>
                </div>
                {erreurCreation && (
                    <div className="pl-bandeau pl-bandeau-warn" style={{ margin: '8px 0 0' }}>
                        <i className="fa-solid fa-triangle-exclamation" /> {erreurCreation}
                    </div>
                )}
            </>}
        >
            {loading && <div className="loading"><div className="spinner"></div></div>}

            {nonPlaces.length > 0 && (
                    <div className="pl-evt-section">
                        <h4 className="pl-evt-sec">
                            <i className="fa-solid fa-inbox" /> À placer ({nonPlaces.length})
                            <span className="pl-evt-sec-hint">Glissez vers le calendrier</span>
                        </h4>
                        <div className="pl-evt-list">
                            {nonPlaces.map(evt => {
                                const cat = getCatInfo(evt);
                                return (
                                    <div
                                        key={evt.id}
                                        ref={isHighlighted(evt.id) ? highlightRef : null}
                                        className={'pl-evt-item pl-evt-draggable' + (isHighlighted(evt.id) ? ' pl-evt-highlight' : '')}
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, evt)}
                                        style={{ borderLeftColor: cat.couleur }}
                                    >
                                        <span className="pl-evt-ic" style={{ color: cat.couleur }}><i className={'fa-solid ' + cat.icone} /></span>
                                        {editingId === evt.id ? (
                                            <div className="pl-evt-edit">
                                                <input type="text" value={editingLibelle}
                                                    onChange={(e) => setEditingLibelle(e.target.value)}
                                                    onKeyDown={(e) => { if (e.key === 'Enter') handleRename(evt.id); if (e.key === 'Escape') cancelEdit(); }}
                                                    autoFocus className="pl-f pl-evt-edit-input"
                                                />
                                                <button className="pl-icon-btn" onClick={() => handleRename(evt.id)} title="Valider"><i className="fa-solid fa-check" /></button>
                                                <button className="pl-icon-btn" onClick={cancelEdit} title="Annuler"><i className="fa-solid fa-xmark" /></button>
                                            </div>
                                        ) : (
                                            <>
                                                <span className="pl-evt-lbl">{evt.libelle}</span>
                                                <span className="pl-evt-cat-badge" style={{ background: cat.couleur }}>{cat.libelle}</span>
                                                <div className="pl-evt-acts">
                                                    <button className="pl-icon-btn" onClick={() => startEdit(evt)} title="Renommer"><i className="fa-solid fa-pen" /></button>
                                                    <button className="pl-icon-btn" onClick={() => handleDelete(evt.id)} title="Supprimer"><i className="fa-solid fa-trash" /></button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {places.length > 0 && (
                    <div className="pl-evt-section">
                        <h4 className="pl-evt-sec"><i className="fa-solid fa-check" /> Placés ({places.length})</h4>
                        <div className="pl-evt-list">
                            {places.map(evt => {
                                const cat = getCatInfo(evt);
                                return (
                                    <div
                                        key={evt.id}
                                        ref={isHighlighted(evt.id) ? highlightRef : null}
                                        className={'pl-evt-item pl-evt-place' + (isHighlighted(evt.id) ? ' pl-evt-highlight' : '')}
                                        style={{ borderLeftColor: cat.couleur }}
                                        onMouseEnter={() => setHoveredPlacedId(evt.id)}
                                        onMouseLeave={() => setHoveredPlacedId(null)}
                                    >
                                        <span className="pl-evt-ic" style={{ color: cat.couleur }}><i className={'fa-solid ' + cat.icone} /></span>
                                        {editingId === evt.id ? (
                                            <div className="pl-evt-edit">
                                                <input type="text" value={editingLibelle}
                                                    onChange={(e) => setEditingLibelle(e.target.value)}
                                                    onKeyDown={(e) => { if (e.key === 'Enter') handleRename(evt.id); if (e.key === 'Escape') cancelEdit(); }}
                                                    autoFocus className="pl-f pl-evt-edit-input"
                                                />
                                                <button className="pl-icon-btn" onClick={() => handleRename(evt.id)}><i className="fa-solid fa-check" /></button>
                                                <button className="pl-icon-btn" onClick={cancelEdit}><i className="fa-solid fa-xmark" /></button>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="pl-evt-lbl-groupe">
                                                    <span className="pl-evt-lbl">{evt.libelle}</span>
                                                    {hoveredPlacedId === evt.id ? (
                                                        <span className="pl-evt-date">{formatDate(evt.date_debut)}</span>
                                                    ) : (
                                                        <span className="pl-evt-date">{formatDateShort(evt.date_debut)}</span>
                                                    )}
                                                </div>
                                                <div className="pl-evt-acts">
                                                    <button className="pl-icon-btn" onClick={() => startEdit(evt)} title="Renommer"><i className="fa-solid fa-pen" /></button>
                                                    <button className="pl-icon-btn" onClick={() => handleDissocier(evt)} title="Dissocier"><i className="fa-solid fa-link-slash" /></button>
                                                    <button className="pl-icon-btn" onClick={() => handleDelete(evt.id)} title="Supprimer"><i className="fa-solid fa-trash" /></button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {evenements.length === 0 && !loading && (
                    <div className="pl-evt-vide">
                        <p>Aucun événement créé.</p>
                        <p>Créez-en un ci-dessus puis glissez-le sur le calendrier.</p>
                    </div>
                )}

                {/* Récup N-1 — en bas de la liste */}
                {prevAnneeId && (
                    <div className="pl-recup">
                        {!showRecup ? (
                            <button className="pl-btn pl-btn-sm pl-recup-btn" onClick={handleOpenRecup}>
                                <i className="fa-solid fa-arrow-up-from-bracket" /> Récupérer les événements N-1
                            </button>
                        ) : (
                            <div className="pl-recup-panel">
                                <div className="pl-recup-h">
                                    <span>Événements de l'année précédente</span>
                                    <button className="pl-btn pl-btn-sm" onClick={() => setShowRecup(false)} title="Fermer"><i className="fa-solid fa-xmark" /></button>
                                </div>
                                {erreurRecup && (
                                    <div className="pl-bandeau pl-bandeau-warn" style={{ margin: '8px 10px' }}>
                                        <i className="fa-solid fa-triangle-exclamation" /> {erreurRecup}
                                    </div>
                                )}
                                {loadingRecup ? (
                                    <div style={{ textAlign: 'center', padding: 8 }}><div className="spinner"></div></div>
                                ) : prevEvents.length === 0 ? (
                                    <div className="pl-recup-vide">Aucun événement en N-1.</div>
                                ) : (
                                    <>
                                        <div className="pl-recup-tout">
                                            <button className="pl-btn pl-btn-sm" onClick={() => {
                                                if (selectedRecupIds.length === prevEvents.length) {
                                                    setSelectedRecupIds([]);
                                                } else {
                                                    setSelectedRecupIds(prevEvents.map(e => e.id));
                                                }
                                            }}>
                                                {selectedRecupIds.length === prevEvents.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                                            </button>
                                        </div>
                                        <div className="pl-recup-list">
                                            {prevEvents.map(evt => {
                                                const cat = getCatInfo(evt);
                                                const alreadyExists = evenements.some(e => e.libelle === evt.libelle);
                                                const checked = selectedRecupIds.includes(evt.id);
                                                return (
                                                    <label
                                                        key={evt.id}
                                                        className={'pl-recup-item' + (alreadyExists ? ' pl-recup-item-deja' : '')}
                                                        onClick={() => !alreadyExists && toggleRecupId(evt.id)}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={!alreadyExists && checked}
                                                            disabled={alreadyExists}
                                                            onChange={() => {}}
                                                        />
                                                        <span style={{ color: cat.couleur }}><i className={'fa-solid ' + cat.icone} /></span>
                                                        <span>{evt.libelle}</span>
                                                        {alreadyExists && <span className="pl-recup-deja">déjà présent</span>}
                                                    </label>
                                                );
                                            })}
                                        </div>
                                        {(() => {
                                            const importableCount = selectedRecupIds.filter(id => {
                                                const e = prevEvents.find(x => x.id === id);
                                                return e && !evenements.some(ex => ex.libelle === e.libelle);
                                            }).length;
                                            return (
                                                <button
                                                    className="pl-btn pl-btn-success pl-btn-sm pl-recup-import"
                                                    onClick={handleImportRecup}
                                                    disabled={importableCount === 0 || importingRecup}
                                                >
                                                    {importingRecup
                                                        ? <><i className="fa-solid fa-spinner fa-spin" /> Importation…</>
                                                        : <><i className="fa-solid fa-arrow-up-from-bracket" /> Importer ({importableCount})</>}
                                                </button>
                                            );
                                        })()}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                )}
        </PanneauFlottant>
    );
}

export default EventManager;